import $api from './instance';
import { Ticket, TicketDetails, CreateTicketDto, UpdateTicketDto, ResolveTicketBody } from '@/types/tickets';

interface TicketsResponse {
  tickets: Ticket[];
  total: number;
}

export const getTickets = async (filters: any): Promise<TicketsResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters.propertyId && filters.propertyId !== 'all-properties') {
      params.append('propertyId', filters.propertyId);
    }
    if (filters.tenantId && filters.tenantId !== 'all-tenants') {
      params.append('tenantId', filters.tenantId);
    }
    if (filters.status && filters.status !== 'all-statuses') {
      params.append('status', filters.status);
    }
    if (filters.category && filters.category !== 'all-categories') {
      params.append('category', filters.category);
    }
    if (filters.priority && filters.priority !== 'all-priorities') {
      params.append('priority', filters.priority);
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters.offset || filters.offset === 0) {
      params.append('offset', filters.offset.toString());
    }

    const queryString = params.toString();
    const url = queryString ? `/tickets?${queryString}` : '/tickets';

    const response = await $api.get(url);

    if (response.data?.success && Array.isArray(response.data.data) && response.data.meta) {
      return {
        tickets: response.data.data,
        total: response.data.meta.total,
      };
    }

    return { tickets: [], total: 0 };

  } catch (error) {
    console.error('Error fetching tickets:', error);
    throw error;
  }
};

export const getTicketById = async (id: string): Promise<TicketDetails | null> => {
    try {
        const response = await $api.get(`/tickets/${id}`);
        return response.data.data;
    } catch (error) {
        console.error(`Error fetching ticket details for id ${id}:`, error);
        throw error;
    }
};

export const createTicket = async (ticketData: CreateTicketDto): Promise<Ticket> => {
  try {
    const response = await $api.post('/tickets', ticketData);
    return response.data.data;
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw error;
  }
};

export const updateTicket = async (id: string, data: UpdateTicketDto): Promise<Ticket> => {
  try {
    const response = await $api.put(`/tickets/${id}`, data);
    return response.data.data;
  } catch (error) {
    console.error(`Error updating ticket ${id}:`, error);
    throw error;
  }
};

export const resolveTicket = async (id: string, body: ResolveTicketBody): Promise<Ticket> => {
  try {
    const response = await $api.post(`/tickets/${id}/resolve`, body);
    return response.data.data;
  } catch (error) {
    console.error(`Error resolving ticket ${id}:`, error);
    throw error;
  }
};

export const closeTicket = async (id: string): Promise<Ticket> => {
  try {
    const response = await $api.post(`/tickets/${id}/close`);
    return response.data.data;
  } catch (error) {
    console.error(`Error closing ticket ${id}:`, error);
    throw error;
  }
};

export const reopenTicket = async (id: string): Promise<Ticket> => {
  try {
    const response = await $api.post(`/tickets/${id}/reopen`);
    return response.data.data;
  } catch (error) {
    console.error(`Error reopening ticket ${id}:`, error);
    throw error;
  }
};

export const startProgressTicket = async (id: string): Promise<Ticket> => {
  try {
    const response = await $api.post(`/tickets/${id}/start-progress`);
    return response.data.data;
  } catch (error) {
    console.error(`Error starting progress on ticket ${id}:`, error);
    throw error;
  }
};

export const holdTicket = async (id: string): Promise<Ticket> => {
  try {
    const response = await $api.post(`/tickets/${id}/hold`);
    return response.data.data;
  } catch (error) {
    console.error(`Error holding ticket ${id}:`, error);
    throw error;
  }
};

export const deleteTicket = async (id: string): Promise<void> => {
  try {
    await $api.delete(`/tickets/${id}`);
  } catch (error) {
    console.error(`Error deleting ticket ${id}:`, error);
    throw error;
  }
};

export const getFullReportRequest = async (filters: any) => {
  console.log('Fetching full report with filters:', filters);
  return Promise.resolve({ data: '<html><body><h1>Tickets Report (Placeholder)</h1></body></html>' });
};
